import React, { useRef } from "react";
import { createPurchaseUnitDraft } from "../../utils/purchaseUnitDraft";

const sameBarcode = (left, right) =>
  String(left || "").trim().toLocaleLowerCase() ===
  String(right || "").trim().toLocaleLowerCase();

export default function PurchaseUnitDraftEditor({
  units,
  receivingUnitKey,
  scannedBarcode,
  disabled = false,
  errors = {},
  onChange,
  onReceivingUnitChange,
}) {
  const nextKey = useRef(1);

  const updateUnit = (key, field, value) => {
    onChange(units.map((unit) =>
      unit.key === key ? { ...unit, [field]: value } : unit));
  };

  const selectReceivingUnit = (key) => {
    onChange(units.map((unit) => {
      if (unit.key === key) return { ...unit, barcode: scannedBarcode };
      if (unit.key === receivingUnitKey && sameBarcode(unit.barcode, scannedBarcode)) {
        return { ...unit, barcode: "" };
      }
      return unit;
    }));
    onReceivingUnitChange(key);
  };

  const addUnit = () => {
    const key = `unit-${nextKey.current}`;
    nextKey.current += 1;
    onChange([...units, createPurchaseUnitDraft({ key })]);
  };

  const removeUnit = (key) => {
    const remaining = units.filter((unit) => unit.key !== key);
    if (key === receivingUnitKey) {
      const fallback = remaining.find((unit) => unit.isBaseUnit) || remaining[0];
      onChange(remaining.map((unit) =>
        unit.key === fallback?.key ? { ...unit, barcode: scannedBarcode } : unit));
      onReceivingUnitChange(fallback?.key || "");
      return;
    }
    onChange(remaining);
  };

  return (
    <section className="purchase-unit-editor" aria-labelledby="purchase-unit-editor-title">
      <div className="purchase-unit-editor-toolbar">
        <div>
          <h3 id="purchase-unit-editor-title">Packaging units</h3>
          <p>Use any unit names. Every unit shares one base-stock balance.</p>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          data-add-purchase-unit
          onClick={addUnit}
          disabled={disabled}
        >
          + Add another unit
        </button>
      </div>

      {(errors.base || errors.names || errors.barcodes || errors.receivingUnit) && (
        <div className="form-error purchase-unit-editor-error" role="alert">
          {[errors.base, errors.names, errors.barcodes, errors.receivingUnit]
            .filter(Boolean).join(" ")}
        </div>
      )}

      <div className="purchase-unit-list">
        {units.map((unit, index) => {
          const prefix = `purchase-unit-${unit.key}`;
          const isReceiving = unit.key === receivingUnitKey;
          return (
            <div
              className={`purchase-unit-row ${isReceiving ? "is-receiving" : ""}`}
              data-purchase-unit-row
              data-unit-key={unit.key}
              key={unit.key}
            >
              <label className="purchase-unit-receiving" htmlFor={`${prefix}-receiving`}>
                <input
                  id={`${prefix}-receiving`}
                  type="radio"
                  name="receivingUnit"
                  checked={isReceiving}
                  onChange={() => selectReceivingUnit(unit.key)}
                  disabled={disabled}
                />
                Scanned
              </label>
              <label htmlFor={`${prefix}-name`}>
                Unit name
                <input
                  id={`${prefix}-name`}
                  name="unitName"
                  type="text"
                  value={unit.unitName}
                  onChange={(event) => updateUnit(unit.key, "unitName", event.target.value)}
                  placeholder={unit.isBaseUnit ? "Bottle, Piece, Sachet…" : "Pack, Tray, Carton…"}
                  disabled={disabled}
                />
              </label>
              <label htmlFor={`${prefix}-factor`}>
                {unit.isBaseUnit ? "Base factor" : "Contains base units"}
                <input
                  id={`${prefix}-factor`}
                  name="conversionFactor"
                  type="number"
                  min="1"
                  step="1"
                  value={unit.isBaseUnit ? "1" : unit.conversionFactor}
                  onChange={(event) => updateUnit(unit.key, "conversionFactor", event.target.value)}
                  disabled={disabled || unit.isBaseUnit}
                />
              </label>
              <label htmlFor={`${prefix}-barcode`}>
                Unit barcode
                <input
                  id={`${prefix}-barcode`}
                  name="unitBarcode"
                  type="text"
                  value={unit.barcode}
                  onChange={(event) => updateUnit(unit.key, "barcode", event.target.value)}
                  placeholder={isReceiving ? "Scanned barcode" : "Generated if blank"}
                  readOnly={isReceiving}
                  disabled={disabled}
                />
              </label>
              {!unit.isBaseUnit && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  data-remove-purchase-unit
                  onClick={() => removeUnit(unit.key)}
                  disabled={disabled}
                  aria-label={`Remove ${unit.unitName || "unit"}`}
                >
                  Remove
                </button>
              )}
              {errors.rows?.[index]?.length > 0 && (
                <ul className="unit-definition-errors" role="alert">
                  {errors.rows[index].map((message) => <li key={message}>{message}</li>)}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
